import React, { useState, useEffect } from 'react';
import { useCalendar } from './useCalendar';
import { ChevronIcon } from '../appearence/icons/chevron_icon';
import { ONE_DAY } from '../constant';
import {
    getDate,
    getUserTimezone,
    getZonedParts,
    parseDateInput,
    zonedDateFromParts,
} from '../components/helper';

const checkDateIsEqual = (date1, date2) => getDate(date1) === getDate(date2);

const checkIsToday = (date) => checkDateIsEqual(date, Date.now());

export const createDate = (params = {}) => {
    const timezone = params.timezone || getUserTimezone();
    const locale = params?.locale ?? 'ru-RU';
    const instant = parseDateInput(params?.date ?? Date.now(), timezone);
    const parts = getZonedParts(instant, timezone);
    const day = instant.toLocaleDateString(locale, { weekday: 'long', timeZone: timezone });
    const dayShort = instant.toLocaleDateString(locale, { weekday: 'short', timeZone: timezone });
    const yearShort = instant.toLocaleDateString(locale, { year: '2-digit', timeZone: timezone });
    const month = instant.toLocaleDateString(locale, { month: 'long', timeZone: timezone });
    const monthShort = instant.toLocaleDateString(locale, { month: 'short', timeZone: timezone });
    const week = getWeekNumber(instant, timezone);

    return {
        date: instant,
        dayNumber: parts.dayNumber,
        day,
        dayNumberInWeek: parts.weekdayIndex + 1,
        dayShort,
        year: parts.year,
        yearShort,
        month,
        monthShort,
        monthNumber: parts.monthNumber,
        monthIndex: parts.monthIndex,
        timestamp: instant.getTime(),
        week,
        timezone,
    };
};

export const createMonth = (params = {}) => {
    const timezone = params.timezone || getUserTimezone();
    const locale = params?.locale ?? 'default';
    const base = createDate({ date: params?.date ?? Date.now(), locale, timezone });
    const { month: monthName, year, monthNumber, monthIndex } = base;

    const getDay = (dayNumber) =>
        createDate({
            date: zonedDateFromParts(year, monthIndex, dayNumber, timezone),
            locale,
            timezone,
        });

    const createMonthDays = () => {
        const days = [];
        for (let i = 0; i <= getMonthNumberOfDays(monthIndex, year) - 1; i += 1) {
            days[i] = getDay(i + 1);
        }
        return days;
    };

    return {
        getDay,
        monthName,
        monthIndex,
        monthNumber,
        year,
        createMonthDays,
        timezone,
    };
};

export const createYear = (params = {}) => {
    const timezone = params.timezone || getUserTimezone();
    const locale = params?.locale ?? 'default';
    const monthCount = 12;
    const today = createDate({ timezone, locale });
    const year = params?.year ?? today.year;
    const monthNumber = params?.monthNumber ?? today.monthNumber;
    const month = createMonth({
        date: zonedDateFromParts(year, monthNumber - 1, 1, timezone),
        locale,
        timezone,
    });

    const getMonthDays = (monthIndex) =>
        createMonth({
            date: zonedDateFromParts(year, monthIndex, 1, timezone),
            locale,
            timezone,
        }).createMonthDays();

    const createYearMonthes = () => {
        const monthes = [];
        for (let i = 0; i <= monthCount - 1; i += 1) {
            monthes[i] = getMonthDays(i);
        }
        return monthes;
    };

    return {
        createYearMonthes,
        month,
        year,
    };
};

export const getMonthNumberOfDays = (
    monthIndex,
    yearNumber = getZonedParts().year,
) => new Date(Date.UTC(yearNumber, monthIndex + 1, 0)).getUTCDate();

export const getMonthesNames = (locale = 'ru-RU') => {
    const timezone = getUserTimezone();
    const monthesNames = Array.from({ length: 12 }, () => ({
        month: 'month',
        monthShort: 'monthShort',
        monthIndex: 'monthIndex',
        date: 'date',
    }));
    const today = getZonedParts(Date.now(), timezone);

    for (let i = 0; i < 12; i += 1) {
        const { month, monthIndex, monthShort, date } = createDate({
            locale,
            date: zonedDateFromParts(today.year, i, 1, timezone),
            timezone,
        });
        monthesNames[monthIndex] = { month, monthIndex, monthShort, date };
    }

    return monthesNames;
};

export const getWeekDaysNames = (firstWeekDay = 4, locale = 'ru-RU') => {
    const timezone = getUserTimezone();
    const weekDaysNames = Array.from({ length: 7 }, () => ({
        day: ['day'],
        dayShort: ['dayShort'],
    }));
    const today = getZonedParts(Date.now(), timezone);

    for (let i = 0; i < 7; i += 1) {
        const { day, dayNumberInWeek, dayShort } = createDate({
            locale,
            date: zonedDateFromParts(today.year, today.monthIndex, today.dayNumber + i, timezone),
            timezone,
        });
        weekDaysNames[dayNumberInWeek - 1] = { day, dayShort };
    }

    return [...weekDaysNames.slice(firstWeekDay - 1), ...weekDaysNames.slice(0, firstWeekDay - 1)];
};

export const getWeekNumber = (date, timezone = getUserTimezone()) => {
    const parts = getZonedParts(date, timezone);
    const firstDayOfTheYear = zonedDateFromParts(parts.year, 0, 1, timezone);
    const firstWeekday = getZonedParts(firstDayOfTheYear, timezone).weekdayIndex;
    const pastDaysOfYear = (new Date(date).getTime() - firstDayOfTheYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstWeekday + 1) / 7);
};

const findDayBetweenStartAndDay = (day, startDate, endDate) => {
    if (!startDate || Number.isNaN(startDate.getTime?.() ?? +startDate)) {
        return false;
    }
    const start = parseDateInput(startDate);
    const end = endDate && !Number.isNaN(endDate.getTime?.() ?? +endDate)
        ? parseDateInput(endDate)
        : null;
    if (checkDateIsEqual(day.date, start)) {
        return true;
    }
    if (end && checkDateIsEqual(day.date, end)) {
        return true;
    }
    if (end && day.date > start && day.date < end) {
        return true;
    }
    return false;
};

const Calendar = ({
    locale = 'ru-RU',
    selectedDate: date,
    currentDate,
    setCurrentDate,
    firstWeekDayNumber = 2,
}) => {
    const { functions, state } = useCalendar({
        locale,
        selectedDate: date,
        firstWeekDayNumber,
    });
    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        if (currentDate.startDate && !checkDateIsEqual(parseDateInput(currentDate.startDate), state.selectedDay.date)) {
            functions.setSelectedDay(createDate({ date: currentDate.startDate, locale }));
        }
        if (!currentDate.startDate) {
            setCurrentDate({ ...currentDate, startDate: state.selectedDay.timestamp });
        }
        if (currentDate.endDate) {
            functions.setEndDay(createDate({ date: currentDate.endDate, locale }));
        }
    }, []);

    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
        } else if (!currentDate.startDate || currentDate.startDate > state.selectedDay.timestamp) {
            setCurrentDate({
                ...currentDate,
                startDate: state.selectedDay.timestamp,
                id: 'today',
                endDate: currentDate.endDate
                    ? currentDate.endDate
                    : state.selectedDay.timestamp + ONE_DAY - 60 * 1000,
            });
        } else if (currentDate.startDate === state.selectedDay.timestamp) {
            setCurrentDate({ ...currentDate, startDate: '', endDate: '' });
        } else {
            setCurrentDate({
                ...currentDate,
                endDate: state.selectedDay.timestamp,
                id: (state.selectedDay.timestamp - currentDate.startDate) / ONE_DAY > 2 ? 'week' : 'today',
            });
            functions.setEndDay(state.selectedDay);
        }
    }, [state.selectedDay]);

    return (
        <>
            <div className='calendar__header'>
                <div aria-hidden onClick={() => functions.onClickArrow('left')}>
                    <ChevronIcon nameOfClass='calendar__header-arrow_left'/>
                </div>
                <h2 className="block__content calendar__title">
                    {state.monthesNames[state.selectedMonth.monthIndex].month} {state.selectedYear}
                </h2>
                <div aria-hidden onClick={() => functions.onClickArrow('right')}>
                    <ChevronIcon nameOfClass='calendar__header-arrow_right'/>
                </div>
            </div>
            <div className='calendar__body'>
                <div className='block__text calendar__days'>
                    {state.calendarDays.map((day) => {
                        const isToday = checkIsToday(day.date);
                        const isSelectedDay = findDayBetweenStartAndDay(
                            day,
                            currentDate.startDate ? new Date(currentDate.startDate) : null,
                            currentDate.endDate ? new Date(currentDate.endDate) : null,
                        );
                        const isAdditionalDay = day.monthIndex !== state.selectedMonth.monthIndex;
                        return (
                            <div
                                key={`${day.dayNumber}-${day.monthIndex}`}
                                aria-hidden
                                onClick={() => {
                                    functions.setSelectedDay(day);
                                }}
                                className={[
                                    'calendar__day',
                                    isToday ? 'calendar__today' : '',
                                    isSelectedDay ? 'calendar__selected' : '',
                                    isAdditionalDay ? 'calendar__additional' : '',
                                ].join(' ')}
                            >
                                {day.dayNumber}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default Calendar;
